import React, { useState, useEffect, useCallback } from 'react';
import JSZip from 'jszip';
import { documentAPI } from '../../../services/api';
import PhotoUpload from '../PhotoUpload';
import PhotoLightbox from '../PhotoLightbox';

const fetchPhotoBlob = async (photo) => {
    const { data } = await documentAPI.getDownloadUrl(photo.id);
    if (!data.downloadUrl) throw new Error('No download URL');
    const response = await fetch(data.downloadUrl);
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
    return response.blob();
};

const downloadSinglePhoto = async (photo) => {
    const blob = await fetchPhotoBlob(photo);
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = photo.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
};

const downloadPhotosAsZip = async (photosToDownload, zipFileName) => {
    const zip = new JSZip();
    const usedNames = new Set();

    await Promise.allSettled(
        photosToDownload.map(async (photo) => {
            try {
                const blob = await fetchPhotoBlob(photo);
                let name = photo.fileName;
                if (usedNames.has(name)) {
                    const dot = name.lastIndexOf('.');
                    const base = dot > 0 ? name.slice(0, dot) : name;
                    const ext = dot > 0 ? name.slice(dot) : '';
                    let counter = 1;
                    while (usedNames.has(name)) {
                        name = `${base} (${counter})${ext}`;
                        counter++;
                    }
                }
                usedNames.add(name);
                zip.file(name, blob);
            } catch (err) {
                console.error(`Failed to fetch ${photo.fileName}:`, err);
            }
        })
    );

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const blobUrl = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = zipFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
};

const PhotosTab = ({ photos, projectId, projectName, onRefresh }) => {
    const [thumbnails, setThumbnails] = useState({});
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [deleting, setDeleting] = useState(false);

    const selectionMode = selected.size > 0;

    const loadThumbnails = useCallback(async () => {
        const urls = {};
        await Promise.allSettled(
            photos.map(async (photo) => {
                try {
                    const { data } = await documentAPI.getDownloadUrl(photo.id);
                    urls[photo.id] = data.downloadUrl;
                } catch {
                    urls[photo.id] = null;
                }
            })
        );
        setThumbnails(urls);
    }, [photos]);

    useEffect(() => {
        if (photos.length > 0) loadThumbnails();
    }, [photos, loadThumbnails]);

    // Clear selection when photos change (after delete/upload)
    useEffect(() => {
        setSelected(new Set());
    }, [photos]);

    const toggleSelect = (photoId, e) => {
        e.stopPropagation();
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(photoId)) next.delete(photoId);
            else next.add(photoId);
            return next;
        });
    };

    const handleSelectAll = () => {
        if (selected.size === photos.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(photos.map(p => p.id)));
        }
    };

    const handleClearSelection = () => setSelected(new Set());

    const handleDownloadAll = async () => {
        setDownloading(true);
        try {
            if (photos.length === 1) {
                await downloadSinglePhoto(photos[0]);
            } else {
                await downloadPhotosAsZip(photos, `${projectName} - Photos.zip`);
            }
        } catch (err) {
            console.error('Download failed:', err);
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadSelected = async () => {
        setDownloading(true);
        try {
            const selectedPhotos = photos.filter(p => selected.has(p.id));
            if (selectedPhotos.length === 1) {
                await downloadSinglePhoto(selectedPhotos[0]);
            } else {
                await downloadPhotosAsZip(selectedPhotos, `${projectName} - Photos.zip`);
            }
        } catch (err) {
            console.error('Download failed:', err);
        } finally {
            setDownloading(false);
        }
    };

    const handleDeleteSelected = async () => {
        const count = selected.size;
        if (!window.confirm(`Delete ${count} photo${count > 1 ? 's' : ''}? This cannot be undone.`)) return;

        setDeleting(true);
        try {
            for (const id of selected) {
                try { await documentAPI.delete(id); }
                catch (err) { console.error(`Failed to delete photo ${id}:`, err); }
            }
            setSelected(new Set());
            if (onRefresh) onRefresh();
        } finally {
            setDeleting(false);
        }
    };

    const handleThumbnailClick = (photo, idx) => {
        if (selectionMode) {
            setSelected(prev => {
                const next = new Set(prev);
                if (next.has(photo.id)) next.delete(photo.id);
                else next.add(photo.id);
                return next;
            });
        } else {
            setLightboxIndex(idx);
        }
    };

    return (
        <div>
            <div className="tab-header">
                <h3>Photos ({photos.length})</h3>
                {photos.length > 0 && !selectionMode && (
                    <div className="tab-header-actions">
                        <button
                            className="btn-download-all"
                            onClick={handleDownloadAll}
                            disabled={downloading}
                        >
                            <i className={`fas ${downloading ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                            {downloading ? 'Downloading...' : 'Download All'}
                        </button>
                    </div>
                )}
            </div>

            {/* Selection Toolbar */}
            {selectionMode && (
                <div className="photo-selection-bar">
                    <div className="selection-info">
                        <span className="selection-count">{selected.size} selected</span>
                        <button className="btn-select-toggle" onClick={handleSelectAll}>
                            {selected.size === photos.length ? 'Deselect All' : 'Select All'}
                        </button>
                        <button className="btn-select-toggle" onClick={handleClearSelection}>
                            Cancel
                        </button>
                    </div>
                    <div className="selection-actions">
                        <button
                            className="btn-selection-action btn-selection-download"
                            onClick={handleDownloadSelected}
                            disabled={downloading || deleting}
                        >
                            <i className={`fas ${downloading ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                            Download
                        </button>
                        <button
                            className="btn-selection-action btn-selection-delete"
                            onClick={handleDeleteSelected}
                            disabled={downloading || deleting}
                        >
                            <i className={`fas ${deleting ? 'fa-spinner fa-spin' : 'fa-trash-alt'}`}></i>
                            Delete
                        </button>
                    </div>
                </div>
            )}

            <PhotoUpload projectId={projectId} onUploadComplete={onRefresh} />

            {photos.length === 0 ? (
                <div className="tab-empty">
                    <i className="fas fa-camera"></i>
                    <p>No photos uploaded yet</p>
                    <p>Upload project photos to keep track of work progress</p>
                </div>
            ) : (
                <div className="photo-grid">
                    {photos.map((photo, idx) => (
                        <div
                            key={photo.id}
                            className={`photo-thumbnail${selected.has(photo.id) ? ' photo-selected' : ''}`}
                            onClick={() => handleThumbnailClick(photo, idx)}
                        >
                            {/* Checkbox */}
                            <div
                                className={`photo-check${selected.has(photo.id) ? ' checked' : ''}`}
                                onClick={(e) => toggleSelect(photo.id, e)}
                            >
                                {selected.has(photo.id) && <i className="fas fa-check"></i>}
                            </div>

                            {thumbnails[photo.id] ? (
                                <img src={thumbnails[photo.id]} alt={photo.fileName} />
                            ) : (
                                <div className="photo-thumbnail-loading">
                                    <i className="fas fa-image"></i>
                                </div>
                            )}
                            <div className="photo-filename">{photo.fileName}</div>
                        </div>
                    ))}
                </div>
            )}

            {lightboxIndex !== null && (
                <PhotoLightbox
                    photos={photos}
                    initialIndex={lightboxIndex}
                    projectId={projectId}
                    onClose={() => setLightboxIndex(null)}
                    onDelete={onRefresh}
                    onSaved={onRefresh}
                />
            )}
        </div>
    );
};

export default PhotosTab;
